import React, {useEffect, useState, useRef} from 'react';
import './styles.css';

export type TextInputUITypes = 'standart' | 'box';

interface FileExtensionsInputProps {
  value: string[];
  heading: string;
  onChange: (extensions: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  maxCharacters?: number;
  className?: string;
  uiType?: TextInputUITypes;
}

const FileExtensionsInput: React.FC<FileExtensionsInputProps> = ({
  value,
  heading,
  onChange,
  disabled = false,
  placeholder = 'e.g. jpg, png; pdf',
  maxCharacters,
  className,
  uiType = 'standart',
}) => {
  const [rawInput, setRawInput] = useState<string>('');
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Normalize individual extension
  const formatExtension = (ext: string): string => {
    const trimmed = ext.trim().replace(/^\*?\.?/, '');
    return trimmed ? `.${trimmed}` : '';
  };

  // Convert raw input string to normalized list
  const parseExtensions = (input: string): string[] => {
    const split = input.split(/[,;]+/);
    const cleaned = split
      .map(formatExtension)
      .filter((ext, index, arr) => ext && arr.indexOf(ext) === index);
    return cleaned;
  };

  // Convert list back to input string
  const stringifyExtensions = (extensions: string[]): string => {
    return extensions.join(', ');
  };

  // Keep rawInput in sync with value prop
  useEffect(() => {
    setRawInput(stringifyExtensions(value));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.value;

    if (maxCharacters && inputValue.length > maxCharacters) {
      inputValue = inputValue.slice(0, maxCharacters);
    }

    setRawInput(inputValue);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      const parsed = parseExtensions(inputValue);
      onChange(parsed); // parent is responsible for updating `value` prop
    }, 1000);
  };

  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  return (
    <div
      className={`${className} FileExtensionsInput ${
        disabled ? 'InputDisabled' : ''
      }`}>
      <label htmlFor="extensions-input" className="InputTitle">
        {heading}
      </label>
      <input
        id="extensions-input"
        className={uiType == 'box' ? 'TextInputBox' : 'TextInputStandart'}
        type="text"
        value={rawInput}
        onChange={handleChange}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxCharacters}
      />
    </div>
  );
};

export default FileExtensionsInput;
